<template>
    <BaseLayout :seo="seo">
        <Breadcrumbs :items="breadcrumbs"/>

        <section class="pt-5 pb-10" typeof="schema:Product">
            <Container>
                <div class="grid lg:grid-cols-2 gap-4 mb-4">
                    <ProductHeader :product="product" :reviews="reviews">
                        <div>
                            <div v-for="(attribute, slug) in product.attributes" class="py-2 border-b last:border-none last:pb-0">
                                <div v-if="slug === 'brand'" rel="schema:brand">
                                    <div typeof="schema:Brand">
                                        <div property="schema:name" :content="attribute.value"></div>
                                    </div>
                                </div>
                                <div class="flex justify-between">
                                    <p class="text-gray-500 mr-3">{{ attribute.title }}</p>
                                    <p class="font-semibold text-right">{{ attribute.value }}</p>
                                </div>
                            </div>
                        </div>
                    </ProductHeader>
                    <div class="space-y-5">
                        <div class="hidden lg:block section p-4">
                            <ProductTitle :product="product" :reviews="reviews"/>
                        </div>

                        <!--                            <p class="font-bold mb-2">Pariatur ex aliqua elit ut enim consequat amet non do ut. Ad aute-->
                        <!--                                deserunt fugiat qui Lorem in quis velit labore voluptate.</p>-->

                            <div class="flex flex-wrap sm:flex-nowrap items-center" rel="schema:offers">
                                <div class="w-full *:p-4 section" typeof="schema:Offer">
                                    <div v-if="!product.is_certificate" class="border-b-2 text-sm font-bold">
                                        <span class="font-gray-300">Продавець: </span>
                                        <Link :href="route('companies.products', {company: product.company})"
                                              class="link">
                                            {{ product.company.title }}
                                        </Link>
                                    </div>

                                    <div v-if="product.is_active && product.modifications.length" class="flex flex-wrap border-b-2">
                                        <Modifications
                                          :modifications="product.modifications"
                                          :selected-options="addToCartForm.modifications"
                                          :variants="product.variants"
                                          :product-variant="productVariant"
                                          @changed="modificationsChanged"
                                        />

                                        <InputError class="mt-2" :message="addToCartForm.errors.modifications"/>
                                    </div>

                                    <div>
                                        <div class="sm:flex mb-5 sm:justify-between space-y-3 sm:space-y-0">
                                            <div class="flex items-center justify-between sm:w-1/2 sm:pr-2.5">
                                                <div>
                                                    <span class="inline-block px-3 py-1 text-xl font-bold bg-green-500 rounded-md">
                                                        {{ calculatedPrice?.formatted || product.price.formatted }}
                                                    </span>

                                                    <ProductSchema :product="product"/>
                                                    <div property="schema:price" :content="product.price.amount / 100"></div>
                                                    <div rel="schema:hasMerchantReturnPolicy">
                                                        <div typeof="schema:MerchantReturnPolicy">
                                                            <div property="schema:applicableCountry" content="UA"></div>
                                                            <div property="schema:returnPolicyCategory"
                                                                 content="https://schema.org/MerchantReturnFiniteReturnWindow"></div>
                                                            <div property="schema:merchantReturnDays" content="14"></div>
                                                            <div property="schema:returnMethod"
                                                                 content="https://schema.org/ReturnByMail"></div>
                                                            <div property="schema:returnFees"
                                                                 content="https://schema.org/ReturnShippingFees"></div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <Number
                                                      v-if="product.is_active && (!productVariant || productVariant.is_available)"
                                                      v-model="addToCartForm.quantity"
                                                      :min="1"
                                                      :max="100"
                                                      size="sm"
                                                      @update:model-value="quantityUpdated"
                                                    />
                                                    <p v-else class="text-sm font-bold">Немає в наявності</p>
                                                </div>
                                            </div>

                                            <div class="flex items-center text-sm" v-if="(calculatedBonus || product.bonus).amount > 0">
                                                <Coin class="w-6 text-fuchsia-500"/>
                                                <span class="ml-1 mt-1">
                                                    <span class="font-bold">+{{ calculatedBonus?.formatted || product.bonus.formatted }} бонусів</span>
                                                </span>
                                                <InfoModal class="w-5 ml-2" title="Бонусний рахунок">
                                                    <div class="flex items-start gap-5">
                                                        <Coin class="w-10 text-fuchsia-500 flex-none"/>
                                                        <div class="space-y-2">
                                                            <p>Накопичуй бонусні гривні та використовуй їх при оплаті своїх покупок.</p>
                                                            <p>Для нарахування бонусів необхідно бути авторизованим на момент підтвердження замовлення.</p>
                                                            <p>Бонусні гривні не нараховуються при купівлі товарів з застосованою знижкою чи в оплату частинами.</p>
                                                            <p>Нараховані бонуси необхідно використати протягом 180 днів після нарахування.</p>
                                                        </div>
                                                    </div>
                                                </InfoModal>
                                            </div>
                                        </div>

                                        <div v-if="product.is_active && productVariant && !productVariant.is_available" class="w-full">
                                            <Alert type="warning" class="text-sm">
                                                <p>Товар в цій комбінації недоступний 🤷</p>
                                                <p>Будь ласка, оберіть доступну комбінацію</p>
                                            </Alert>
                                        </div>
                                        <div v-else-if="product.is_active && (!productVariant || productVariant.is_available)" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <PrimaryButton @click="addToCart" color="indigo" class="min-w-36">
                                                До кошика
                                            </PrimaryButton>
                                            <MonoCheckoutButton @click="monoCheckout" class="min-w-42"/>
                                        </div>
                                        <div v-else-if="product.category" class="w-full sm:w-64 pl-1">
                                            <Link :href="route('categories.show', product.category)">
                                                <PrimaryButton color="green">
                                                    До категорії {{ product.category.title }}
                                                </PrimaryButton>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                                <!--                                    <div class="w-auto px-2 mb-4">-->
                                <!--                                        <a class="inline-flex items-center justify-center w-12 h-12 text-black hover:text-indigo-500 border-2 border-black hover:border-indigo-500 rounded-md transition duration-200" href="#">-->
                                <!--                                            <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-7-1">-->
                                <!--                                                <path d="M14.44 0.0999756C12.63 0.0999756 11.01 0.979976 10 2.32998C8.99 0.979976 7.37 0.0999756 5.56 0.0999756C2.49 0.0999756 0 2.59998 0 5.68998C0 6.87998 0.19 7.97998 0.52 8.99998C2.1 14 6.97 16.99 9.38 17.81C9.72 17.93 10.28 17.93 10.62 17.81C13.03 16.99 17.9 14 19.48 8.99998C19.81 7.97998 20 6.87998 20 5.68998C20 2.59998 17.51 0.0999756 14.44 0.0999756Z" fill="currentColor"></path>-->
                                <!--                                            </svg>-->
                                <!--                                        </a>-->
                                <!--                                    </div>-->
                                <!--                                    <div class="w-auto px-2 mb-4">-->
                                <!--                                        <a class="inline-flex items-center justify-center w-12 h-12 text-black hover:text-indigo-500 border-2 border-black hover:border-indigo-500 rounded-md transition duration-200" href="#">-->
                                <!--                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-8-1">-->
                                <!--                                                <path d="M20.36 12.7299C19.99 12.7299 19.68 12.45 19.64 12.08C19.4 9.87995 18.22 7.89995 16.4 6.63995C16.07 6.40995 15.99 5.95995 16.22 5.62995C16.45 5.29995 16.9 5.21995 17.23 5.44995C19.4 6.95995 20.8 9.31995 21.09 11.93C21.13 12.33 20.84 12.6899 20.44 12.7299C20.41 12.7299 20.39 12.7299 20.36 12.7299Z" fill="currentColor"></path>-->
                                <!--                                                <path d="M3.74001 12.78C3.72001 12.78 3.69001 12.78 3.67001 12.78C3.27001 12.74 2.98001 12.38 3.02001 11.98C3.29001 9.36996 4.67001 7.00996 6.82001 5.48996C7.14001 5.25996 7.60001 5.33996 7.83001 5.65996C8.06001 5.98996 7.98001 6.43996 7.66001 6.66996C5.86001 7.94996 4.69001 9.92996 4.47001 12.12C4.43001 12.5 4.11001 12.78 3.74001 12.78Z" fill="currentColor"></path>-->
                                <!--                                                <path d="M15.99 21.1C14.76 21.69 13.44 21.99 12.06 21.99C10.62 21.99 9.24998 21.67 7.96998 21.02C7.60998 20.85 7.46998 20.41 7.64998 20.05C7.81998 19.69 8.25998 19.55 8.61998 19.72C9.24998 20.04 9.91998 20.26 10.6 20.39C11.52 20.57 12.46 20.58 13.38 20.42C14.06 20.3 14.73 20.09 15.35 19.79C15.72 19.62 16.16 19.76 16.32 20.13C16.5 20.49 16.36 20.93 15.99 21.1Z" fill="currentColor"></path>-->
                                <!--                                                <path d="M12.05 2.01001C10.5 2.01001 9.22998 3.27001 9.22998 4.83001C9.22998 6.39001 10.49 7.65001 12.05 7.65001C13.61 7.65001 14.87 6.39001 14.87 4.83001C14.87 3.27001 13.61 2.01001 12.05 2.01001Z" fill="currentColor"></path>-->
                                <!--                                                <path d="M5.05001 13.87C3.50001 13.87 2.23001 15.13 2.23001 16.69C2.23001 18.25 3.49001 19.51 5.05001 19.51C6.61001 19.51 7.87001 18.25 7.87001 16.69C7.87001 15.13 6.60001 13.87 5.05001 13.87Z" fill="currentColor"></path>-->
                                <!--                                                <path d="M18.95 13.87C17.4 13.87 16.13 15.13 16.13 16.69C16.13 18.25 17.39 19.51 18.95 19.51C20.51 19.51 21.77 18.25 21.77 16.69C21.77 15.13 20.51 13.87 18.95 13.87Z" fill="currentColor"></path>-->
                                <!--                                            </svg>-->
                                <!--                                        </a>-->
                                <!--                                    </div>-->
                            </div>
                            <div class="section text-sm">
                                <div class="space-y-2">
                                    <p class="font-bold inline-flex items-center border-b-2 w-full py-3 px-4">
                                        <Truck class="flex-none" width="25"/>
                                        <span class="ml-3">Доставка</span>
                                    </p>
                                    <div class="p-4 pt-1 flex items-center space-x-3"
                                         v-for="shippingMethod in product.company.shipping_methods">
                                        <Component
                                            class="flex-none"
                                            width="20"
                                            :is="shippingIconsMap[shippingMethod.type]"
                                        />

                                        <div>
                                            <div class="flex space-x-7">
                                                <div class="space-y-1">
                                                    <p>До відділення / поштомату Нової Пошти або курʼєром</p>

                                                    <span
                                                        v-if="shippingMethod.free_shipping_rules.length"
                                                        class="bg-green-600 inline-block px-1 font-bold"
                                                    >Безкоштовно:</span>
                                                    <div
                                                        v-for="freeShippingRule in shippingMethod.free_shipping_rules"
                                                        class="flex space-x-7">
                                                        <p v-if="freeShippingRule.type === 'min_amount'">
                                                            - для покупок від
                                                            {{ freeShippingRule.data.amount.formatted }}
                                                            <span v-if="freeShippingRule.data.categories?.length">
                                                                    з {{
                                                                    freeShippingRule.data.categories.length > 1 ? 'категорій ' : 'категорії '
                                                                }}
                                                                </span>
                                                            <Link
                                                                class="text-indigo-500"
                                                                :href="freeShippingRuleUrl(freeShippingRule)"
                                                            >
                                                                <template
                                                                    v-for="(category, key) in freeShippingRule.data.categories">
                                                                    <span v-text="category.title"/>
                                                                    <span
                                                                        v-if="key + 1 < freeShippingRule.data.categories.length">, </span>
                                                                </template>
                                                                у {{ product.company.title }}
                                                            </Link>
                                                        </p>
                                                        <p v-else-if="freeShippingRule.type === 'items_count'">
                                                            - при замовленні від
                                                            {{ freeShippingRule.data.items_count }} товарів з
                                                            {{
                                                                freeShippingRule.data.categories.length > 1 ? 'категорій' : 'категорії'
                                                            }}
                                                            <Link
                                                                class="text-indigo-500"
                                                                :href="route('companies.products', {company: product.company, category: freeShippingRule.data.categories.map(category => category.slug).join(',')})"
                                                            >
                                                                <template
                                                                    v-for="(category, key) in freeShippingRule.data.categories">
                                                                    <span v-text="category.title"/>
                                                                    <span
                                                                        v-if="key + 1 < freeShippingRule.data.categories.length">, </span>
                                                                </template>
                                                                у {{ product.company.title }}
                                                            </Link>
                                                        </p>
                                                    </div>

                                                    <p
                                                        v-if="shippingMethod.estimated_sending"
                                                        class="font-bold"
                                                        v-text="shippingMethod.estimated_sending"
                                                    />
                                                </div>

                                                <div class="flex-none">
                                                    {{ shippingMethod.price }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="section text-sm">
                                <PaymentOptions class="border-b-2"/>
                                <div class="py-3 px-4 inline-flex items-center">
                                    <RadioOn class="flex-none" width="25"/>
                                    <span class="ml-3 mt-1">
                                        <span class="font-bold"> Обмін/повернення</span> товару впродовж 14 днів
                                    </span>
                                </div>
                            </div>
                            <!--                                <span class="block text-sm font-black mb-2" data-config-id="auto-txt-11-1">Share on Social Media</span>-->
                            <!--                                <div>-->
                            <!--                                    <a class="inline-block text-green-500 hover:text-indigo-500 mr-6" href="#">-->
                            <!--                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-9-1">-->
                            <!--                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M13.6349 20.7272V12.766H16.3583L16.7669 9.66243H13.6349V7.68126C13.6349 6.78299 13.8882 6.17083 15.203 6.17083L16.8771 6.17015V3.39421C16.5876 3.35731 15.5938 3.27271 14.4371 3.27271C12.0217 3.27271 10.3681 4.71878 10.3681 7.37389V9.66243H7.63647V12.766H10.3681V20.7272H13.6349Z" fill="currentColor"></path>-->
                            <!--                                            <mask id="mask0_601_10113" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="7" y="3" width="10" height="18">-->
                            <!--                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M13.6349 20.7272V12.766H16.3583L16.7669 9.66243H13.6349V7.68126C13.6349 6.78299 13.8882 6.17083 15.203 6.17083L16.8771 6.17015V3.39421C16.5876 3.35731 15.5938 3.27271 14.4371 3.27271C12.0217 3.27271 10.3681 4.71878 10.3681 7.37389V9.66243H7.63647V12.766H10.3681V20.7272H13.6349Z" fill="white"></path>-->
                            <!--                                            </mask>-->
                            <!--                                        </svg>-->
                            <!--                                    </a>-->
                            <!--                                    <a class="inline-block text-green-500 hover:text-indigo-500 mr-6" href="#">-->
                            <!--                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-10-1">-->
                            <!--                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M21.8181 6.14598C21.1356 6.44844 20.4032 6.65356 19.6336 6.74513C20.4194 6.27462 21.0208 5.52831 21.3059 4.64177C20.5689 5.0775 19.7553 5.39389 18.8885 5.56541C18.1943 4.82489 17.2069 4.36365 16.1118 4.36365C14.0108 4.36365 12.3072 6.06719 12.3072 8.16707C12.3072 8.46489 12.3408 8.75577 12.4057 9.03392C9.24434 8.87513 6.44104 7.3605 4.56483 5.05895C4.23686 5.61986 4.05028 6.27344 4.05028 6.9711C4.05028 8.29107 4.72243 9.45574 5.74225 10.1371C5.11877 10.1163 4.53237 9.94477 4.01901 9.65968V9.70719C4.01901 11.5498 5.33086 13.0876 7.07031 13.4376C6.75161 13.5234 6.41555 13.5709 6.06789 13.5709C5.82222 13.5709 5.58464 13.5466 5.35171 13.5002C5.8361 15.0125 7.24067 16.1123 8.90483 16.1424C7.6034 17.1623 5.96243 17.7683 4.1801 17.7683C3.87301 17.7683 3.57052 17.7498 3.27271 17.7162C4.95655 18.7974 6.95561 19.4279 9.10416 19.4279C16.1026 19.4279 19.928 13.6312 19.928 8.60398L19.9153 8.11147C20.6627 7.57834 21.3094 6.90853 21.8181 6.14598Z" fill="currentColor"></path>-->
                            <!--                                            <mask id="mask0_601_10114" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="3" y="4" width="19" height="16">-->
                            <!--                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M21.8181 6.14598C21.1356 6.44844 20.4032 6.65356 19.6336 6.74513C20.4194 6.27462 21.0208 5.52831 21.3059 4.64177C20.5689 5.0775 19.7553 5.39389 18.8885 5.56541C18.1943 4.82489 17.2069 4.36365 16.1118 4.36365C14.0108 4.36365 12.3072 6.06719 12.3072 8.16707C12.3072 8.46489 12.3408 8.75577 12.4057 9.03392C9.24434 8.87513 6.44104 7.3605 4.56483 5.05895C4.23686 5.61986 4.05028 6.27344 4.05028 6.9711C4.05028 8.29107 4.72243 9.45574 5.74225 10.1371C5.11877 10.1163 4.53237 9.94477 4.01901 9.65968V9.70719C4.01901 11.5498 5.33086 13.0876 7.07031 13.4376C6.75161 13.5234 6.41555 13.5709 6.06789 13.5709C5.82222 13.5709 5.58464 13.5466 5.35171 13.5002C5.8361 15.0125 7.24067 16.1123 8.90483 16.1424C7.6034 17.1623 5.96243 17.7683 4.1801 17.7683C3.87301 17.7683 3.57052 17.7498 3.27271 17.7162C4.95655 18.7974 6.95561 19.4279 9.10416 19.4279C16.1026 19.4279 19.928 13.6312 19.928 8.60398L19.9153 8.11147C20.6627 7.57834 21.3094 6.90853 21.8181 6.14598Z" fill="white"></path>-->
                            <!--                                            </mask>-->
                            <!--                                        </svg>-->
                            <!--                                    </a>-->
                            <!--                                    <a class="inline-block text-green-500 hover:text-indigo-500 mr-6" href="#">-->
                            <!--                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-11-1">-->
                            <!--                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.6007 2.18176H16.3992C19.3874 2.18176 21.8184 4.61276 21.8182 7.60069V16.3992C21.8182 19.3871 19.3874 21.8181 16.3992 21.8181H7.6007C4.61276 21.8181 2.18188 19.3872 2.18188 16.3992V7.60069C2.18188 4.61276 4.61276 2.18176 7.6007 2.18176ZM16.3993 20.0759C18.4267 20.0759 20.0761 18.4266 20.0761 16.3992H20.076V7.60069C20.076 5.57343 18.4266 3.924 16.3992 3.924H7.6007C5.57343 3.924 3.92412 5.57343 3.92412 7.60069V16.3992C3.92412 18.4266 5.57343 20.076 7.6007 20.0759H16.3993ZM6.85721 12.0001C6.85721 9.16418 9.16425 6.85709 12.0001 6.85709C14.8359 6.85709 17.1429 9.16418 17.1429 12.0001C17.1429 14.8358 14.8359 17.1428 12.0001 17.1428C9.16425 17.1428 6.85721 14.8358 6.85721 12.0001ZM8.62805 11.9999C8.62805 13.8592 10.1408 15.3718 12.0001 15.3718C13.8593 15.3718 15.3721 13.8592 15.3721 11.9999C15.3721 10.1405 13.8594 8.62784 12.0001 8.62784C10.1407 8.62784 8.62805 10.1405 8.62805 11.9999Z" fill="currentColor"></path>-->
                            <!--                                            <mask id="mask0_601_10115" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="2" y="2" width="20" height="20">-->
                            <!--                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.6007 2.18176H16.3992C19.3874 2.18176 21.8184 4.61276 21.8182 7.60069V16.3992C21.8182 19.3871 19.3874 21.8181 16.3992 21.8181H7.6007C4.61276 21.8181 2.18188 19.3872 2.18188 16.3992V7.60069C2.18188 4.61276 4.61276 2.18176 7.6007 2.18176ZM16.3993 20.0759C18.4267 20.0759 20.0761 18.4266 20.0761 16.3992H20.076V7.60069C20.076 5.57343 18.4266 3.924 16.3992 3.924H7.6007C5.57343 3.924 3.92412 5.57343 3.92412 7.60069V16.3992C3.92412 18.4266 5.57343 20.076 7.6007 20.0759H16.3993ZM6.85721 12.0001C6.85721 9.16418 9.16425 6.85709 12.0001 6.85709C14.8359 6.85709 17.1429 9.16418 17.1429 12.0001C17.1429 14.8358 14.8359 17.1428 12.0001 17.1428C9.16425 17.1428 6.85721 14.8358 6.85721 12.0001ZM8.62805 11.9999C8.62805 13.8592 10.1408 15.3718 12.0001 15.3718C13.8593 15.3718 15.3721 13.8592 15.3721 11.9999C15.3721 10.1405 13.8594 8.62784 12.0001 8.62784C10.1407 8.62784 8.62805 10.1405 8.62805 11.9999Z" fill="white"></path>-->
                            <!--                                            </mask>-->
                            <!--                                        </svg>-->
                            <!--                                    </a>-->
                            <!--                                    <a class="inline-block text-green-500 hover:text-indigo-500" href="#">-->
                            <!--                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-config-id="auto-svg-12-1">-->
                            <!--                                            <path d="M19.2 3H4.8C3.81 3 3 3.81 3 4.8V19.2C3 20.19 3.81 21 4.8 21H19.2C20.19 21 21 20.19 21 19.2V4.8C21 3.81 20.19 3 19.2 3ZM8.4 18.3H5.7V10.2H8.4V18.3ZM7.05 8.67C6.15 8.67 5.43 7.95 5.43 7.05C5.43 6.15 6.15 5.43 7.05 5.43C7.95 5.43 8.67 6.15 8.67 7.05C8.67 7.95 7.95 8.67 7.05 8.67ZM18.3 18.3H15.6V13.53C15.6 12.81 14.97 12.18 14.25 12.18C13.53 12.18 12.9 12.81 12.9 13.53V18.3H10.2V10.2H12.9V11.28C13.35 10.56 14.34 10.02 15.15 10.02C16.86 10.02 18.3 11.46 18.3 13.17V18.3Z" fill="currentColor"></path>-->
                            <!--                                        </svg>-->
                            <!--                                    </a>-->
                            <!--                                </div>-->
                    </div>
                </div>
                <div
                    property="schema:description"
                    :content="product.description ? product.description.substring(0, 1000) : seo.description"
                ></div>

                <div v-if="product.description" class="mb-4">
                    <h2 class="text-xl font-bold mb-3">Опис</h2>

                    <div class="section p-4 product-description" :class="{'whitespace-pre-line': !containsHtmlTags()}">
                        <div v-html="product.description" class="space-y-3"></div>
                    </div>
                </div>

                <Comments
                  class="mb-4"
                  :product="product"
                  :reviews-data="reviews"
                  :comment="comment"
                />

                <ViewedProducts/>
            </Container>
        </section>
    </BaseLayout>
</template>

<script>
import {defineComponent} from 'vue'
import BaseLayout from "@/Layouts/BaseLayout.vue";
import { router, Link, useForm } from '@inertiajs/vue3';
import PrimaryButton from "@/Components/PrimaryButton.vue";
import InputLabel from "@/Components/InputLabel.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import TextInput from "@/Components/TextInput.vue";
import InputError from "@/Components/InputError.vue";
import ResponsiveImage from "@/Components/ResponsiveImage.vue";
import {createToast, withProps} from 'mosha-vue-toastify';
import ProductAdded from "@/Components/Toasts/ProductAdded.vue";
import {useApiForm} from "@/Helpers/useApiForm/useApiForm";
import ProductTitle from "@/Partials/Products/ProductTitle.vue";
import {ecommerce} from "@/Helpers/zaraz";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import NewPost from "@/Components/Svg/NewPost.vue";
import Truck from "@/Components/Svg/Truck.vue";
import CreditCards from "@/Components/Svg/CreditCards.vue";
import RadioOn from "@/Components/Svg/RadioOn.vue";
import User from "@/Components/Svg/User.vue";
import Message from "@/Components/Svg/Message.vue";
import Comments from "@/Partials/Products/Comments/Comments.vue";
import Rating from "@/Components/Products/Rating.vue";
import ViewedProducts from "@/Partials/Products/ViewedProducts.vue";
import Modifications from "@/Partials/Products/Modifications.vue";
import ProductHeader from "@/Partials/Products/ProductHeader.vue";
import ProductSchema from "@/Partials/Products/ProductSchema.vue";
import Container from "@/Components/Base/Container.vue";
import PaymentOptions from "@/Partials/Products/PaymentOptions.vue";
import Alert from "@/Components/Alert.vue";
import Coin from '@/Components/Svg/Coin.vue';
import Info from '@/Components/Svg/Info.vue';
import InfoModal from '@/Components/InfoModal.vue';
import MonoCheckoutButton from '@/Components/Buttons/MonoCheckoutButton.vue';
import Number from '@/Components/Forms/Number.vue';

export default defineComponent({
    computed: {
        modifications() {
            return Modifications
        }
    },
    components: {
        Number,
        MonoCheckoutButton,
        InfoModal,
        Info,
        Coin,
        Alert,
        PaymentOptions,
        Container,
        ProductSchema,
        ProductHeader,
        Modifications,
        ViewedProducts,
        Rating,
        Comments,
        Message,
        User,
        RadioOn,
        CreditCards,
        Truck,
        NewPost,
        Breadcrumbs,
        ProductTitle,
        ResponsiveImage,
        InputError, TextInput, SecondaryButton, InputLabel, PrimaryButton, BaseLayout, Link
    },
    props: {
        seo: Object,
        product: Object,
        productVariant: Object,
        reviews: Object,
        comment: Object
    },
    data() {
        return {
            addToCartForm: useApiForm({
                quantity: 1,
                modifications: {}
            }),
            breadcrumbs: [
                {
                    url: this.route('categories.show', this.product.category),
                    title: this.product.category.title,
                },
                {
                    title: this.product.title
                }
            ],
            shippingIconsMap: {
                new_post: 'NewPost'
            },
            calculatedPrice: null,
            calculatedBonus: null,
        }
    },
    methods: {
        addToCart() {
            this.addToCartForm.post(this.route('cart.store', this.product.id), {
                preserveScroll: true,
                onSuccess: () => {
                    createToast(withProps(ProductAdded, {
                        backLink: this.route('categories.show', {category: this.product.category}),
                        categoryTitle: this.product.category.title,
                        cartEvent: () => this.emitter.emit('openCart')
                    }), {
                        position: 'bottom-right',
                        hideProgressBar: true
                    })

                    // TODO: add modifications to analytic
                    ecommerce('Product Added', this.product, {
                        quantity: this.addToCartForm.quantity,
                    })

                    router.reload({
                        only: ['cartProductsCount']
                    })

                    this.addToCartForm.quantity = 1
                }
            })
        },
        freeShippingRuleUrl(freeShippingRule) {
            const params = {
                company: this.product.company,
            }

            if (freeShippingRule.data.categories?.length) {
                params.category = freeShippingRule.data.categories.map(category => category.slug).join(',')
            }

            return this.route('companies.products', params)
        },
        containsHtmlTags() {
            const regex = /<[^>]+>/;
            return regex.test(this.product.description);
        },
        modificationsChanged() {
            const variants = this.product.variants;

            if (variants) {
                const variantSlug = Object.values(this.addToCartForm.modifications)
                    .map(modification => modification.options[0])
                    .sort((a, b) => a - b)
                    .join('-')

                if (variants[variantSlug]) {
                    router.visit(this.route('products.show', [this.product, variants[variantSlug].id]), {
                        only: ['productVariant'],
                        preserveState: true,
                        preserveScroll: true,
                    })
                }
            }
        },
        quantityUpdated(quantity) {
            if (quantity === 1) {
                this.calculatedPrice = this.product.price
                this.calculatedBonus = this.product.bonus
            } else {
                useApiForm()
                  .get(this.route('products.calculate_price', { product: this.product, quantity }), {
                      onSuccess: (response) => {
                          this.calculatedPrice = response.price;
                          this.calculatedBonus = response.bonus;
                      }
                  })
            }
        },
        monoCheckout() {
            useForm(this.addToCartForm.data())
              .post(this.route('cart.mono_checkout', this.product.id))
        }
    },
    mounted() {
        ecommerce('Product Viewed', this.product, {
            category: this.product.category.title,
        })
    }
})
</script>

<style>
@reference "../../../css/app.css";

.product-description iframe {
    @apply w-full;
}

.product-description ul {
    @apply list-disc ml-10;
}

.product-description h2 {
    @apply text-2xl font-bold;
}

.product-description h3 {
    @apply text-xl font-bold;
}
.product-description img {
    @apply !h-auto;
}
</style>
